import type { Role } from '@/lib/auth-context';
import type { MyTeacherProfile, TeacherProfileValues } from '@/services/api/profile/profile';
import { fetchApi } from '@/services/api-client';

export const TEACHERS_PATH = '/teachers';

export type TeacherCategory = {
  id: string;
  name: string;
  color: string | null;
  sortOrder: number;
  teacherCount?: number;
};

export type TeacherLinkedUser = {
  id: string;
  email: string;
  role: Role;
};

export type Teacher = {
  id: string;
  name: string;
  role: string;
  photoUrl: string;
  categoryId: string | null;
  category: TeacherCategory | null;
  qualifications: string[];
  certifications: string[];
  bio: string;
  sortOrder: number;
  published: boolean;
  showInLeadership: boolean;
  showInDivisionalPrincipals: boolean;
  userId: string | null;
  user: TeacherLinkedUser | null;
  pendingSubmittedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateTeacherData = {
  name: string;
  role: string;
  photoUrl?: string;
  categoryId?: string | null;
  qualifications?: string[];
  certifications?: string[];
  bio?: string;
  sortOrder?: number;
  published?: boolean;
  showInLeadership?: boolean;
  showInDivisionalPrincipals?: boolean;
  userId?: string | null;
};

export type UpdateTeacherData = Partial<CreateTeacherData>;

export type TeacherSubmissionReview = MyTeacherProfile & {
  userId: string;
  email: string;
  isNew: boolean;
};

export const teachersApi = {
  list: () => fetchApi<Teacher[]>(`${TEACHERS_PATH}/admin`),
  getById: (id: string) => fetchApi<Teacher>(`${TEACHERS_PATH}/admin/by-id/${id}`),
  create: (data: CreateTeacherData) => fetchApi<Teacher>(TEACHERS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateTeacherData) =>
    fetchApi<Teacher>(`${TEACHERS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`${TEACHERS_PATH}/${id}`, { method: 'DELETE' }),
  togglePublished: (id: string) => fetchApi<Teacher>(`${TEACHERS_PATH}/${id}/publish`, { method: 'PATCH' }),
  reorder: (orderedIds: string[]) =>
    fetchApi<{ success: boolean }>(`${TEACHERS_PATH}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    }),
  listCategories: () => fetchApi<TeacherCategory[]>(`${TEACHERS_PATH}/categories`),
  createCategory: (data: { name: string; color?: string; sortOrder?: number }) =>
    fetchApi<TeacherCategory>(`${TEACHERS_PATH}/categories`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateCategory: (id: string, data: { name?: string; color?: string; sortOrder?: number }) =>
    fetchApi<TeacherCategory>(`${TEACHERS_PATH}/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteCategory: (id: string) =>
    fetchApi<{ success: boolean }>(`${TEACHERS_PATH}/categories/${id}`, { method: 'DELETE' }),
  listPending: () => fetchApi<TeacherSubmissionReview[]>(`${TEACHERS_PATH}/admin/pending`),
  approve: (id: string, data?: TeacherProfileValues) =>
    fetchApi<Teacher>(`${TEACHERS_PATH}/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    }),
  reject: (id: string, note: string) =>
    fetchApi<Teacher>(`${TEACHERS_PATH}/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
};
